"use client";

import { useEffect, useRef } from "react";
import {
  FadeIn,
  HomeEyebrow,
  HomeHeading,
  HomeLead,
  HomeSection,
} from "~/components/siteglue/home/section-shell";
import { HomeHeroEmployeeRotator } from "~/components/siteglue/home/home-hero-employee-rotator";
import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";

/**
 * Below-the-fold inspiration: reuses the existing employee card rotator
 * without competing with the homepage hero configurator.
 */
export function SiteGlueHomeEmployeesAtWork({
  roleFilter = false,
}: {
  roleFilter?: boolean;
} = {}) {
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    const el = document.getElementById("employees-at-work");
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (viewedRef.current) return;
        viewedRef.current = true;
        trackSiteGlueEvent("homepage_employee_examples_viewed", {
          source: "home_employees_at_work",
        });
        observer.disconnect();
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <HomeSection id="employees-at-work" tone="brand" className="py-16 sm:py-20">
      <FadeIn className="mx-auto max-w-2xl text-center">
        <HomeEyebrow className="text-[#9cbb5a]">Live examples</HomeEyebrow>
        <HomeHeading className="mx-auto text-white">
          See AI Employees Already at Work
        </HomeHeading>
        <HomeLead className="mx-auto text-slate-300">
          {roleFilter
            ? "A few AI Employees already on the job. Filter by role."
            : "Explore examples by industry, role, and communication channel."}
        </HomeLead>
      </FadeIn>

      <FadeIn className="mx-auto mt-10 max-w-md lg:max-w-lg" delay={0.08}>
        <HomeHeroEmployeeRotator
          analyticsClickEvent="homepage_employee_example_clicked"
          showExampleLabel={false}
          enableRoleFilter={roleFilter}
        />
      </FadeIn>
    </HomeSection>
  );
}
